/**
 * bimWaterTankParts — the WATER TANK's parametric geometry, ported from the
 * house-ifc Python module (`devices/water-tank/water_tank.py`).
 *
 * The demo needs a cistern as a device whose water level is the thing you SEE:
 * the walls read slightly transparent and the water inside is drawn to its own
 * surface, so a `level_percentage` change is legible from outside the tank. The
 * same three shapes a site installs:
 *
 *   ibc              1000 l IBC tote — pallet, translucent PE bottle, steel cage
 *   oval-vertical     standing oval cistern — plinth, lid, neck, cap
 *   oval-horizontal   lying oval cistern on two saddles — manhole, end valve
 *
 * ── The frame ───────────────────────────────────────────────────────────────
 *
 * The module's frame, kept 1:1 (mm → m): origin = the centre of the tank's
 * FOOTPRINT on the floor; +X width, +Y depth, +Z up. The device whose model
 * this is places the pin on the floor, so the tank stands ON its pin — unlike
 * the shade/door families, whose frame is a mounting PLANE and therefore a
 * different conversion (`bimDeviceModel.shadePartsInDeviceFrame`).
 *
 * The lying cistern's water is the ELLIPSE SEGMENT below the surface plane, not
 * a shorter cylinder: a half-full horizontal tank reads as a half-filled tube,
 * and a box or a clipped cylinder would read as a tank inside a tank. That is
 * why a part can be a `prism` carrying its own closed profile.
 *
 * Pure: no Vue, no three.js. `bimWaterTankParts.test.ts` pins the numbers
 * against the module's own output; the renderer
 * (`composables/useBimDeviceModels.ts`) turns the parts into meshes.
 */

import type { Vec3 } from './bimShadeSpec';

export type { Vec3 };

/** The three cistern shapes (`schema.json prefs.shape`). */
export type WaterTankShape = 'ibc' | 'oval-vertical' | 'oval-horizontal';

/** Which shared material a part uses (the module's own style keys). */
export type WaterTankPartMaterial =
  | 'shell'
  | 'water'
  | 'surface'
  | 'steel'
  | 'pallet'
  | 'trim'
  | 'lid';

/**
 * How a part is drawn:
 *   box       — an axis-aligned box, `size` = [dx, dy, dz]
 *   cylinder  — a round/elliptical prism; `size` is its BBOX and `axis` says
 *               which way the extrusion runs (Z = standing, X/Y = lying)
 *   prism     — an arbitrary closed profile on `axis`; `profile` is in metres,
 *               in the plane perpendicular to the axis, relative to `center`
 */
export type WaterTankPartShape = 'box' | 'cylinder' | 'prism';

export interface WaterTankPart {
  /** Stable slot key across levels: 'bottle-wall-n' | 'water' | 'surface' | … */
  key: string;
  material: WaterTankPartMaterial;
  shape: WaterTankPartShape;
  /** Part centre, metres, in the frame above. */
  center: Vec3;
  /** Full extents, metres (a cylinder's bbox; a prism's bbox). */
  size: Vec3;
  /** Cylinders: the extrusion axis (default 'z'). */
  axis?: 'x' | 'y' | 'z';
  /** Prisms: the closed profile, in the plane perpendicular to `axis`. */
  profile?: Array<[number, number]>;
}

const MM = 0.001;

// ─── the module's own constants (mm → m) ────────────────────────────────────

/** `devices/water-tank/water_tank.py` — the IBC half. */
export const WATER_TANK_IBC = {
  w: 1200 * MM,
  d: 1000 * MM,
  palletH: 150 * MM,
  bottleW: 1140 * MM,
  bottleD: 940 * MM,
  bottleH: 950 * MM,
  bottleT: 6 * MM,
  cageT: 40 * MM,
  railH: 22 * MM,
  railZ: [270 * MM, 650 * MM, 1030 * MM] as const,
  lidT: 18 * MM,
  neckD: 240 * MM,
  neckH: 45 * MM,
  capD: 280 * MM,
  capH: 26 * MM,
  valveD: 90 * MM,
  valveL: 70 * MM,
  valveZ: 230 * MM,
} as const;

/** The standing oval cistern. */
export const WATER_TANK_OVAL_V = {
  rx: 550 * MM,
  ry: 450 * MM,
  baseRx: 590 * MM,
  baseRy: 490 * MM,
  baseH: 40 * MM,
  h: 1750 * MM,
  lidT: 20 * MM,
  neckD: 300 * MM,
  neckH: 120 * MM,
  capD: 344 * MM,
  capH: 30 * MM,
  /** The water ellipse's radii (the module's `OV_RX/OV_RY - 10 mm`). */
  waterRx: 540 * MM,
  waterRy: 440 * MM,
  wallInset: 10 * MM,
} as const;

/** The lying oval cistern. */
export const WATER_TANK_OVAL_H = {
  l: 2200 * MM,
  ry: 600 * MM,
  rz: 550 * MM,
  zc: 700 * MM,
  saddleW: 160 * MM,
  saddleD: 980 * MM,
  saddleH: 150 * MM,
  saddleX: 700 * MM,
  manD: 420 * MM,
  manH: 60 * MM,
  manCapD: 480 * MM,
  manCapH: 30 * MM,
  manX: 450 * MM,
  valveD: 90 * MM,
  valveL: 80 * MM,
  /** `_segment_profile(OH_RY - 6, OH_RZ - 6, …)` — the water's own radii. */
  waterInset: 6 * MM,
} as const;

/** The level a tank with no reading is drawn at (the schema's own default). */
export const WATER_TANK_DEFAULT_LEVEL = 55;
/** Ellipse facets — the module's `N_SEG`. */
export const WATER_TANK_SEGMENTS = 48;

/** A stored/typed level, clamped to 0…100 and NaN-safe. */
export function waterTankLevelPct(value: unknown): number {
  // `Number(null)` is 0 — a MISSING level must not read as an empty tank, so
  // only a real number or a non-empty numeric string is a reading.
  let n: number;
  if (typeof value === 'number') n = value;
  else if (typeof value === 'string' && value.trim() !== '') n = Number(value);
  else return WATER_TANK_DEFAULT_LEVEL;
  if (!Number.isFinite(n)) return WATER_TANK_DEFAULT_LEVEL;
  return Math.min(100, Math.max(0, n));
}

/** A stored shape name, or null when it is not one of the three. */
export function waterTankShape(value: unknown): WaterTankShape | null {
  return value === 'ibc' || value === 'oval-vertical' || value === 'oval-horizontal' ? value : null;
}

// ─── part helpers (the module's `_box` / `_ellipse_solid` / …) ──────────────

export function tankBox(
  key: string,
  material: WaterTankPartMaterial,
  x0: number,
  y0: number,
  z0: number,
  dx: number,
  dy: number,
  dz: number,
): WaterTankPart {
  return {
    key,
    material,
    shape: 'box',
    center: [x0 + dx / 2, y0 + dy / 2, z0 + dz / 2],
    size: [dx, dy, dz],
  };
}

/** A standing elliptical cylinder (axis Z) — `_ellipse_solid`. */
export function tankCylinderZ(
  key: string,
  material: WaterTankPartMaterial,
  cx: number,
  cy: number,
  z0: number,
  diaX: number,
  diaY: number,
  height: number,
): WaterTankPart {
  return {
    key,
    material,
    shape: 'cylinder',
    axis: 'z',
    center: [cx, cy, z0 + height / 2],
    size: [diaX, diaY, height],
  };
}

/** A lying elliptical cylinder along X — `_ellipse_x_solid`. */
export function tankCylinderX(
  key: string,
  material: WaterTankPartMaterial,
  x0: number,
  zc: number,
  length: number,
  diaY: number,
  diaZ: number,
  y = 0,
): WaterTankPart {
  return {
    key,
    material,
    shape: 'cylinder',
    axis: 'x',
    center: [x0 + length / 2, y, zc],
    size: [length, diaY, diaZ],
  };
}

/** A round stub along Y (the IBC outlet). */
export function tankCylinderY(
  key: string,
  material: WaterTankPartMaterial,
  x: number,
  y0: number,
  z: number,
  dia: number,
  length: number,
): WaterTankPart {
  return {
    key,
    material,
    shape: 'cylinder',
    axis: 'y',
    center: [x, y0 + length / 2, z],
    size: [dia, length, dia],
  };
}

/**
 * The part of an ellipse below a horizontal plane, as a closed polygon — the
 * module's `_segment_profile`. `zSurface` is relative to the body's axis
 * (0 = the axis); the chord closes the polygon at the surface plane.
 */
export function tankSegmentProfile(
  ry: number,
  rz: number,
  zSurface: number,
  n = WATER_TANK_SEGMENTS,
): Array<[number, number]> {
  const arc: Array<[number, number]> = [];
  for (let k = 0; k < n; k += 1) {
    const a = (2 * Math.PI * k) / n;
    const y = ry * Math.cos(a);
    const z = rz * Math.sin(a);
    if (z <= zSurface) arc.push([y, z]);
  }
  if (arc.length === 0) {
    // An (almost) empty tank still needs a legal profile: a hairline sliver.
    const eps = Math.max(MM, rz * 0.002);
    return [
      [-ry * 0.98, -rz],
      [ry * 0.98, -rz],
      [ry * 0.98, -rz + eps],
      [-ry * 0.98, -rz + eps],
    ];
  }
  arc.sort((a, b) => Math.atan2(a[1], a[0]) - Math.atan2(b[1], b[0]));
  const t = Math.max(-1, Math.min(1, zSurface / rz));
  const yChord = ry * Math.sqrt(Math.max(0, 1 - t * t));
  const kept = arc.filter((p) => p[1] < zSurface - 1e-6);
  // A surface AT the ellipse's bottom leaves a zero-width polygon (the chord is
  // a point): an empty tank still needs a legal profile, so it takes the sliver.
  if (kept.length < 2) {
    const eps = Math.max(MM, rz * 0.002);
    return [
      [-ry * 0.98, -rz],
      [ry * 0.98, -rz],
      [ry * 0.98, -rz + eps],
      [-ry * 0.98, -rz + eps],
    ];
  }
  return [[-yChord, zSurface], ...kept, [yChord, zSurface]];
}

// ─── the three shapes ───────────────────────────────────────────────────────

/**
 * One tank's parts at a level, in the module's frame.
 *
 * Keys are per-mesh slots (unique), so a re-apply can find the same mesh again
 * as the level moves; the module's style keys ride in `material`.
 */
export function waterTankParts(shape: WaterTankShape, levelPct = WATER_TANK_DEFAULT_LEVEL): WaterTankPart[] {
  const level = waterTankLevelPct(levelPct) / 100;
  const out: WaterTankPart[] = [];

  if (shape === 'ibc') {
    const I = WATER_TANK_IBC;
    const x0 = -I.w / 2;
    const y0 = -I.d / 2;
    out.push(tankBox('pallet', 'pallet', x0, y0, 0, I.w, I.d, I.palletH));
    const bx0 = -I.bottleW / 2;
    const by0 = -I.bottleD / 2;
    const zb = I.palletH;
    out.push(tankBox('bottle-bottom', 'shell', bx0, by0, zb, I.bottleW, I.bottleD, I.bottleT));
    out.push(tankBox('bottle-wall-s', 'shell', bx0, by0, zb, I.bottleW, I.bottleT, I.bottleH));
    out.push(
      tankBox('bottle-wall-n', 'shell', bx0, by0 + I.bottleD - I.bottleT, zb, I.bottleW, I.bottleT, I.bottleH),
    );
    out.push(tankBox('bottle-wall-w', 'shell', bx0, by0, zb, I.bottleT, I.bottleD, I.bottleH));
    out.push(
      tankBox('bottle-wall-e', 'shell', bx0 + I.bottleW - I.bottleT, by0, zb, I.bottleT, I.bottleD, I.bottleH),
    );
    // The lid is the bottle's own material: a dark lid over a translucent
    // bottle reads as a grey cabinet on screen and hides the water under it
    // (measured on the demo's first render).
    out.push(tankBox('lid', 'shell', bx0, by0, I.palletH + I.bottleH, I.bottleW, I.bottleD, I.lidT));
    out.push(
      tankCylinderZ('neck', 'trim', 0, 0, I.palletH + I.bottleH + I.lidT, I.neckD, I.neckD, I.neckH),
    );
    out.push(
      tankCylinderZ(
        'cap',
        'trim',
        0,
        0,
        I.palletH + I.bottleH + I.lidT + I.neckH,
        I.capD,
        I.capD,
        I.capH,
      ),
    );
    const px = I.w / 2 - I.cageT / 2;
    const py = I.d / 2 - I.cageT / 2;
    const posts: Array<[number, number]> = [
      [-px, -py],
      [px, -py],
      [-px, py],
      [px, py],
    ];
    posts.forEach(([cx, cy], index) => {
      out.push(
        tankBox(
          `post-${index}`,
          'steel',
          cx - I.cageT / 2,
          cy - I.cageT / 2,
          I.palletH,
          I.cageT,
          I.cageT,
          I.bottleH,
        ),
      );
    });
    I.railZ.forEach((zr, ring) => {
      out.push(tankBox(`rail-${ring}-s`, 'steel', x0, y0, zr, I.w, I.cageT, I.railH));
      out.push(tankBox(`rail-${ring}-n`, 'steel', x0, y0 + I.d - I.cageT, zr, I.w, I.cageT, I.railH));
      out.push(tankBox(`rail-${ring}-w`, 'steel', x0, y0, zr, I.cageT, I.d, I.railH));
      out.push(tankBox(`rail-${ring}-e`, 'steel', x0 + I.w - I.cageT, y0, zr, I.cageT, I.d, I.railH));
    });
    out.push(
      tankBox('top-frame-s', 'steel', x0, y0, I.palletH + I.bottleH, I.w, I.cageT, I.cageT),
    );
    out.push(
      tankBox(
        'top-frame-n',
        'steel',
        x0,
        y0 + I.d - I.cageT,
        I.palletH + I.bottleH,
        I.w,
        I.cageT,
        I.cageT,
      ),
    );
    out.push(
      tankCylinderY('valve', 'trim', 0, I.bottleD / 2, I.valveZ, I.valveD, I.valveL),
    );
    const innerW = I.bottleW - 2 * I.bottleT;
    const innerD = I.bottleD - 2 * I.bottleT;
    const zBot = I.palletH + I.bottleT;
    const usable = I.bottleH - I.bottleT;
    const zTop = zBot + level * usable;
    if (zTop > zBot + 0.0005) {
      out.push(
        tankBox('water', 'water', -innerW / 2, -innerD / 2, zBot, innerW, innerD, zTop - zBot),
      );
      out.push(
        tankBox('surface', 'surface', -innerW / 2, -innerD / 2, zTop - 0.003, innerW, innerD, 0.003),
      );
    }
    return out;
  }

  if (shape === 'oval-vertical') {
    const V = WATER_TANK_OVAL_V;
    out.push(tankCylinderZ('plinth', 'trim', 0, 0, 0, V.baseRx * 2, V.baseRy * 2, V.baseH));
    out.push(tankCylinderZ('body', 'shell', 0, 0, V.baseH, V.rx * 2, V.ry * 2, V.h));
    const top = V.baseH + V.h;
    out.push(tankCylinderZ('lid', 'shell', 0, 0, top, (V.rx + V.wallInset) * 2, (V.ry + V.wallInset) * 2, V.lidT));
    out.push(tankCylinderZ('neck', 'trim', 0, 0, top + V.lidT, V.neckD, V.neckD, V.neckH));
    out.push(tankCylinderZ('cap', 'trim', 0, 0, top + V.lidT + V.neckH, V.capD, V.capD, V.capH));
    const zBot = V.baseH;
    const zTop = zBot + level * V.h;
    if (zTop > zBot + 0.0005) {
      out.push(tankCylinderZ('water', 'water', 0, 0, zBot, V.waterRx * 2, V.waterRy * 2, zTop - zBot));
      out.push(tankCylinderZ('surface', 'surface', 0, 0, zTop - 0.004, V.waterRx * 2, V.waterRy * 2, 0.004));
    }
    return out;
  }

  const H = WATER_TANK_OVAL_H;
  const x0 = -H.l / 2;
  out.push(tankCylinderX('body', 'shell', x0, H.zc, H.l, H.ry * 2, H.rz * 2));
  [-H.saddleX, H.saddleX].forEach((sx, index) => {
    out.push(
      tankBox(
        `saddle-${index}`,
        'trim',
        sx - H.saddleW / 2,
        -H.saddleD / 2,
        0,
        H.saddleW,
        H.saddleD,
        H.saddleH,
      ),
    );
  });
  const crown = H.zc + H.rz;
  out.push(tankCylinderZ('manhole', 'trim', H.manX, 0, crown - 0.002, H.manD, H.manD, H.manH));
  out.push(
    tankCylinderZ('manhole-cap', 'trim', H.manX, 0, crown - 0.002 + H.manH, H.manCapD, H.manCapD, H.manCapH),
  );
  out.push(
    tankCylinderX('valve', 'trim', x0 - H.valveL, H.zc - H.rz * 0.55, H.valveL, H.valveD, H.valveD),
  );
  const zBot = H.zc - H.rz;
  const zTop = zBot + level * 2 * H.rz;
  if (level > 0.005) {
    const profile = tankSegmentProfile(H.ry - H.waterInset, H.rz - H.waterInset, zTop - H.zc);
    out.push({
      key: 'water',
      material: 'water',
      shape: 'prism',
      axis: 'x',
      center: [0, 0, H.zc],
      size: [H.l - 2 * H.waterInset, (H.ry - H.waterInset) * 2, (H.rz - H.waterInset) * 2],
      profile,
    });
    const t = Math.max(-1, Math.min(1, (zTop - H.zc) / (H.rz - H.waterInset)));
    const halfChord = (H.ry - H.waterInset) * Math.sqrt(Math.max(0, 1 - t * t));
    out.push(
      tankBox(
        'surface',
        'surface',
        x0 + H.waterInset,
        -halfChord,
        zTop - 0.004,
        H.l - 2 * H.waterInset,
        2 * halfChord,
        0.004,
      ),
    );
  }
  return out;
}

/** A cylinder's full extents, for a renderer that scales a unit primitive. */
export function tankCylinderScale(part: WaterTankPart): [number, number, number] {
  return [part.size[0], part.size[1], part.size[2]];
}
